import { useState, type DragEvent } from "react";
import { useEditorStore } from "../../stores/editorStore.js";
import type { AnyBlock } from "@juro/shared";
import { Button } from "../ui/Button.js";

const LABELS: Record<AnyBlock["type"], string> = {
  title: "タイトル",
  text: "テキスト",
  spacer: "余白",
  divider: "区切り線",
  kv_block: "入力項目",
  computed_value: "計算",
  grid: "明細",
  row_container: "2カラム",
  settlement: "合計欄",
  free_band: "自由帯",
  recipient: "宛名",
  issuer_info: "発行者情報",
};

function blockLabel(block: AnyBlock): string {
  if (block.label) return block.label;
  if (block.type === "kv_block") return String(block.items[0]?.label || LABELS[block.type]);
  return LABELS[block.type];
}

const ADD_ITEMS = [
  { kind: "title", label: "タイトル" },
  { kind: "recipient", label: "宛名" },
  { kind: "office", label: "会社名" },
  { kind: "registration", label: "士業登録番号" },
  { kind: "invoice_registration", label: "適格番号" },
  { kind: "issuer_name", label: "資格者名" },
  { kind: "issuer_qualification", label: "資格名" },
  { kind: "issuer_business", label: "業務表示" },
  { kind: "issuer_custom", label: "カスタム項目" },
  { kind: "transactionDate", label: "取引年月" },
  { kind: "invoiceAmount", label: "請求額" },
  { kind: "text", label: "テキスト" },
  { kind: "detail", label: "明細" },
  { kind: "row_container", label: "2カラム" },
  { kind: "settlement", label: "合計欄" },
] as const;

export type AddBlockKind = (typeof ADD_ITEMS)[number]["kind"];

type MoveDirection = "up" | "down";

export function BlockList({
  blocks,
  onAddBlock,
  onMoveBlock,
  onReorderBlock,
  onDeleteBlock,
}: {
  blocks: AnyBlock[];
  onAddBlock: (kind: AddBlockKind) => void;
  onMoveBlock: (id: string, direction: MoveDirection) => void;
  onReorderBlock: (draggedId: string, targetId: string) => void;
  onDeleteBlock: (id: string) => void;
}) {
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  function handleDrop(event: DragEvent<HTMLDivElement>, targetId: string) {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    if (draggedId && draggedId !== targetId) onReorderBlock(draggedId, targetId);
  }

  return (
    <aside style={{ width: 220, borderRight: "1px solid #e2e8f0", background: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <div style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {ADD_ITEMS.map((item) => (
            <Button
              key={item.kind}
              style={{ width: "100%", padding: "6px 7px", minHeight: 30, fontSize: 12 }}
              onClick={() => onAddBlock(item.kind)}
            >
              + {item.label}
            </Button>
          ))}
        </div>
      </div>
      <div style={{ padding: 8, overflowY: "auto", flex: 1 }}>
        {blocks.map((block, index) => (
          <div
            key={block.id}
            draggable
            onDragStart={(event) => {
              setDraggingId(block.id);
              event.dataTransfer.setData("text/plain", block.id);
              event.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => handleDrop(event, block.id)}
            onDragEnd={() => setDraggingId(null)}
            style={{
              border: `1px solid ${selectedBlockId === block.id ? "#0f766e" : "#e2e8f0"}`,
              background: selectedBlockId === block.id ? "#f0fdfa" : draggingId === block.id ? "#e2e8f0" : "#f8fafc",
              borderRadius: 8,
              marginBottom: 6,
              overflow: "hidden",
              cursor: "grab",
            }}
          >
            <button
              onClick={() => selectBlock(block.id)}
              style={{
                width: "100%",
                display: "block",
                textAlign: "left",
                border: "none",
                background: "transparent",
                padding: "9px 10px 6px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800 }}>{blockLabel(block)}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{block.id}</div>
            </button>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, padding: "0 8px 8px" }}>
              <Button
                aria-label={`${block.id}を上へ`}
                disabled={index === 0}
                onClick={() => onMoveBlock(block.id, "up")}
                style={{ minHeight: 26, padding: "3px 8px", fontSize: 12 }}
              >
                ↑
              </Button>
              <Button
                aria-label={`${block.id}を下へ`}
                disabled={index === blocks.length - 1}
                onClick={() => onMoveBlock(block.id, "down")}
                style={{ minHeight: 26, padding: "3px 8px", fontSize: 12 }}
              >
                ↓
              </Button>
              <Button
                variant="danger"
                aria-label={`${block.id}を削除`}
                onClick={() => onDeleteBlock(block.id)}
                style={{ minHeight: 26, padding: "3px 8px", fontSize: 12 }}
              >
                削除
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 12, borderTop: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>選択中: {selectedBlockId ?? "なし"}</div>
      </div>
    </aside>
  );
}
